import React from 'react'
import PropTypes from 'prop-types'
import moment from './moment'
import DayCalendarHeader from './DayCalendarHeader'
import DayCalendarDay from './DayCalendarDay'

/**
 * Create a React Day Calendar component
 *
 * @param {import('./interfaces').DayCalendarProps} props
 * @returns {JSX.Element}
 */
function DayCalendar ({
  isDayDisabled: isDisabled,
  isDaySelected: isSelected,
  focusedDay,
  onDaySelected,
  firstDayOfWeek,
  currentDay,
  idPrefix,
  date,
  'aria-labelledby': ariaLabelledby
}) {
  const momentDate = moment(focusedDay || date)
  const lastDayOfMonth = momentDate.clone().endOf('month')
  const firstDayOfMonth = momentDate
    .clone()
    .startOf('month')
    .valueOf()
  const weeks = getCalendarWeeks(
    moment(firstDayOfMonth),
    lastDayOfMonth,
    firstDayOfWeek
  )

  const dayProps = {
    firstDayOfMonth: firstDayOfMonth.valueOf(),
    lastDayOfMonth: lastDayOfMonth.valueOf(),
    onClick: onDaySelected,
    currentDay,
    isSelected,
    isDisabled,
    focusedDay
  }

  return (
    <table
      className='pika-table'
      cellPadding={0}
      cellSpacing={0}
      role='grid'
      aria-labelledby={ariaLabelledby}
    >
      <caption className='zb-slider-description-sr-only'>
        You can browse through the calendar table
      </caption>
      <DayCalendarHeader firstDayOfWeek={firstDayOfWeek} />
      <tbody>
        {weeks.map((week, i) => (
          <tr key={`week-${firstDayOfMonth}-${i}`} className='pika-row'>
            {week.map(offset => (
              <DayCalendarDay
                key={`day-${firstDayOfMonth}-${offset}`}
                {...dayProps}
                offset={offset}
                idPrefix={idPrefix}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * @param {moment.Dayjs} firstDayOfMonth
 * @param {moment.Dayjs} lastDayOfMonth
 * @param {number} firstDayOfWeek
 * @returns {number[][]}
 */
function getCalendarWeeks (firstDayOfMonth, lastDayOfMonth, firstDayOfWeek) {
  const firstDay = (firstDayOfWeek - firstDayOfMonth.day() - 7) % 7
  const lastDay =
    lastDayOfMonth.date() + ((6 - lastDayOfMonth.day() + firstDayOfWeek) % 7)
  /** @type {number[][]} */
  const weeks = []
  for (let i = firstDay, day = 0, week = -1; i < lastDay; i++) {
    if (day === 0) {
      weeks.push([])
      week += 1
    }
    day = (day + 1) % 7

    weeks[week].push(i)
  }
  return weeks
}

const dateProp = PropTypes.instanceOf(Date)

DayCalendar.propTypes = {
  date: dateProp,
  focusedDay: dateProp,
  currentDay: dateProp,
  firstDayOfWeek: PropTypes.number,
  isDaySelected: PropTypes.func,
  isDayDisabled: PropTypes.func,
  onDaySelected: PropTypes.func,
  idPrefix: PropTypes.string
}

DayCalendar.defaultProps = {
  isDaySelected: d => {},
  isDayDisabled: d => {},
  onDaySelected: d => {},
  firstDayOfWeek: 1
}

export default DayCalendar
